import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  CloseButton,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCalendarClock,
  IconHash,
  IconPencil,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ItemListPhoto } from './ItemListPhoto';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { useItemTypes, useUoms } from '../lookups/useLookups';
import { listItems, type ItemDto } from './api';
import { ItemFormModal } from './ItemFormModal';

// Fetch the full set once (active + inactive) and narrow client-side, so the
// Active/All toggle is instant — same approach as the warehouses screen.
const ITEMS_KEY = ['items', 'all'] as const;

type Scope = 'active' | 'all';

export function ItemsRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<ItemDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ITEMS_KEY,
    queryFn: ({ signal }) => listItems(true, signal),
  });

  // Reference data for resolving the id columns to something readable.
  const uoms = useUoms();
  const itemTypes = useItemTypes();

  const uomLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of uoms.data ?? []) map.set(u.uomId, u.uomCode);
    return map;
  }, [uoms.data]);

  const itemTypeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const it of itemTypes.data ?? []) map.set(it.itemTypeId, it.itemTypeCode);
    return map;
  }, [itemTypes.data]);

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((i) => {
      if (scope === 'active' && !i.isActive) return false;
      if (!q) return true;
      return (
        i.itemNumber.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q)
      );
    });
  }, [query.data, scope, search]);

  const inactiveCount = useMemo(
    () => (query.data ?? []).filter((i) => !i.isActive).length,
    [query.data],
  );

  function startCreate() {
    setEditing(null);
    open();
  }
  function startEdit(i: ItemDto) {
    setEditing(i);
    open();
  }
  function onSaved() {
    void queryClient.invalidateQueries({ queryKey: ITEMS_KEY });
  }

  function formatCost(item: ItemDto): string {
    if (item.standardCost === null) return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: item.currencyCode,
      }).format(item.standardCost);
    } catch {
      // Unknown currency code — show the raw number rather than blowing up.
      return `${item.standardCost} ${item.currencyCode}`;
    }
  }


  /**
   * Declared rather than written as table cells, so the handheld card view reads the same labels.
   * The number is the card's headline and the description its subtitle: those are what somebody
   * scans a list for. Standard cost is dropped on a phone — it is frequently 0 because a tenant
   * that does not cost its stock never sets it, and a column of "$0.00" is not worth the height.
   */
  const columns: DataColumn<ItemDto>[] = useMemo(
    () => [
      {
        // Legacy shows the elected photo on its inquiry screens, which is what this is.
        //
        // Hidden on a handheld: one blob fetch per card over a warehouse connection is a real
        // cost, and on that screen the item NUMBER is what an operator is matching against — they
        // have the thing in their hand. Change `hidden` to `aside` if that turns out to be wrong.
        key: 'photo',
        label: '',
        card: 'hidden',
        render: (i) => <ItemListPhoto itemId={i.itemId} defaultImageId={i.defaultImageId} />,
      },
      {
        key: 'number',
        label: t('items.number'),
        card: 'title',
        render: (i) => <Code>{i.itemNumber}</Code>,
      },
      {
        key: 'description',
        label: t('items.description'),
        card: 'subtitle',
        render: (i) => i.description,
      },
      {
        key: 'itemType',
        label: t('items.itemType'),
        render: (i) => (i.itemTypeId ? (itemTypeLabel.get(i.itemTypeId) ?? '—') : '—'),
      },
      {
        key: 'uom',
        label: t('items.uomShort'),
        render: (i) => uomLabel.get(i.primaryUomId) ?? '—',
      },
      {
        key: 'tracking',
        label: t('items.tracking'),
        // Compact badges — the three tracking flags matter at a glance but would cost three
        // columns each.
        render: (i) => (
          <Group gap={4} wrap="nowrap" justify="flex-end">
            {i.isSerialized && (
              <Tooltip label={t('items.isSerialized')}>
                <Badge size="sm" variant="light" color="invetrak" leftSection={<IconHash size={10} />}>
                  {t('items.serializedShort')}
                </Badge>
              </Tooltip>
            )}
            {i.isLotControlled && (
              <Tooltip label={t('items.isLotControlled')}>
                <Badge size="sm" variant="light" color="grape">
                  {t('items.lotShort')}
                </Badge>
              </Tooltip>
            )}
            {i.tracksExpiration && (
              <Tooltip
                label={
                  i.defaultShelfLifeDays
                    ? t('items.expiresWithDays', { count: i.defaultShelfLifeDays })
                    : t('items.tracksExpiration')
                }
              >
                <Badge size="sm" variant="light" color="orange" leftSection={<IconCalendarClock size={10} />}>
                  {t('items.expiresShort')}
                </Badge>
              </Tooltip>
            )}
            {!i.isSerialized && !i.isLotControlled && !i.tracksExpiration && (
              <Text size="xs" c="dimmed">
                —
              </Text>
            )}
          </Group>
        ),
      },
      {
        key: 'standardCost',
        label: t('items.standardCost'),
        align: 'right',
        card: 'hidden',
        render: (i) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCost(i)}</span>
        ),
      },
      {
        key: 'status',
        label: t('items.status'),
        card: 'aside',
        render: (i) => (
          <Badge color={i.isActive ? 'green' : 'gray'} variant="light">
            {i.isActive ? t('items.activeYes') : t('items.activeNo')}
          </Badge>
        ),
      },
    ],
    [t, itemTypeLabel, uomLabel],
  );

  return (
    <>
      <PageHeader
        title={t('nav.items')}
        actions={
          <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
            {t('items.new')}
          </Button>
        }
      />

      {query.isLoading && (
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      )}

      {query.isError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title={t('items.errors.loadFailed')}
        >
          <Stack gap="xs">
            <Text size="sm">{(query.error as Error).message}</Text>
            {query.error instanceof ApiError && query.error.problem.correlationId && (
              <Text size="xs" c="dimmed">
                {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
              </Text>
            )}
            <Group>
              <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                {t('items.retry')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <Card withBorder radius="md" padding="xl">
          <Stack gap="sm" align="center">
            <Text fw={600}>{t('items.emptyTitle')}</Text>
            <Text size="sm" c="dimmed" ta="center">
              {t('items.emptyBody')}
            </Text>
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('items.new')}
            </Button>
          </Stack>
        </Card>
      )}

      {query.isSuccess && query.data.length > 0 && (
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Group gap="sm">
              <SegmentedControl
                value={scope}
                onChange={(v) => setScope(v as Scope)}
                data={[
                  { label: t('items.scopeActive'), value: 'active' },
                  {
                    label:
                      inactiveCount > 0
                        ? t('items.scopeAllCount', { count: inactiveCount })
                        : t('items.scopeAll'),
                    value: 'all',
                  },
                ]}
              />
              <Text size="sm" c="dimmed">
                {t('items.count', { count: rows.length })}
              </Text>
            </Group>
            <TextInput
              placeholder={t('items.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSection={
                search ? (
                  <CloseButton
                    size="sm"
                    onClick={() => setSearch('')}
                    aria-label={t('items.searchClear')}
                  />
                ) : null
              }
              w={{ base: '100%', sm: 280 }}
            />
          </Group>

          <Card withBorder radius="md" padding={0}>
            {rows.length === 0 ? (
              <Center py="xl">
                <Stack gap="xs" align="center">
                  <Text c="dimmed" size="sm">
                    {search.trim() ? t('items.noResults') : t('items.noActive')}
                  </Text>
                  {!search.trim() && scope === 'active' && inactiveCount > 0 && (
                    <Button
                      size="xs"
                      variant="light"
                      color="invetrak"
                      onClick={() => setScope('all')}
                    >
                      {t('items.showAll')}
                    </Button>
                  )}
                </Stack>
              </Center>
            ) : (
              <ResponsiveTable
                rows={rows}
                columns={columns}
                rowKey={(i) => i.itemId}
                minWidth={760}
                actionsWidth={60}
                highlightOnHover
                verticalSpacing="sm"
                horizontalSpacing="md"
                actions={(i) => (
                  <ActionIcon
                    variant="subtle"
                    color="invetrak"
                    aria-label={t('shell.edit')}
                    onClick={() => startEdit(i)}
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                )}
              />
            )}
          </Card>
        </Stack>
      )}

      <ItemFormModal opened={modalOpen} onClose={close} item={editing} onSaved={onSaved} />
    </>
  );
}
